import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import { colors } from "../../colors";
import type { LoadingSpinnerStyleProps } from "./types";

// Keyframe animations for different spinner variants
const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.1); }
`;

const rotateAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const bounceAnimation = keyframes`
  0%, 20%, 53%, 80%, 100% { transform: translate3d(0, 0, 0); }
  40%, 43% { transform: translate3d(0, -30px, 0); }
  70% { transform: translate3d(0, -15px, 0); }
  90% { transform: translate3d(0, -4px, 0); }
`;

const ringAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const foldAnimation = keyframes`
  0%, 10%, 20%, 80%, 90%, 100% { transform: rotateX(0deg); }
  40%, 60% { transform: rotateX(-180deg); }
`;

// Size configurations
const sizeMap = {
  small: { width: "16px", height: "16px", fontSize: "0.9em" },
  medium: { width: "24px", height: "24px", fontSize: "1em" },
  large: { width: "32px", height: "32px", fontSize: "1.1em" },
  xlarge: { width: "48px", height: "48px", fontSize: "1.2em" },
};

// Speed configurations
const speedMap = {
  slow: "2s",
  normal: "1.2s",
  fast: "0.8s",
};

// Color configurations
const colorMap = {
  primary: colors.primary[300],
  secondary: colors.gray[500], // closest match
  success: colors.success,
  warning: colors.warning,
  error: colors.error,
  info: colors.info,
  white: colors.white,
  black: colors.gray[1000],
};

// Main wrapper styled component
export const SpinnerWrapper = styled.div<LoadingSpinnerStyleProps>`
  display: ${({ centered }) => (centered ? "flex" : "inline-flex")};
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;

  ${({ overlay, overlayColor, overlayMode = "fixed" }) =>
    overlay &&
    css`
      position: ${overlayMode};
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: ${overlayColor};
      z-index: ${overlayMode === "fixed" ? "9999" : "999"};
      backdrop-filter: blur(5px);
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}
`;

// Base spinner element
export const SpinnerElement = styled.div<LoadingSpinnerStyleProps>`
  position: relative;
  display: inline-block;

  ${({ size = "medium", width, height }) => {
    const sizeConfig = sizeMap[size];
    return css`
      width: ${width ?? sizeConfig.width};
      height: ${height ?? sizeConfig.height};
    `;
  }}

  ${({ color = "primary" }) => css`
    color: ${colorMap[color]};
  `}
  
  ${({ speed = "normal" }) => css`
    --spinner-speed: ${speedMap[speed]};
  `}
`;

// Pulse variant
export const PulseSpinner = styled(SpinnerElement)`
  background-color: currentColor;
  border-radius: 50%;
  animation: ${pulseAnimation} var(--spinner-speed) ease-in-out infinite;
`;

// Rotate variant
export const RotateSpinner = styled(SpinnerElement)`
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: ${rotateAnimation} var(--spinner-speed) linear infinite;
`;

// Bounce variant
export const BounceSpinner = styled(SpinnerElement)`
  background-color: currentColor;
  border-radius: 50%;
  animation: ${bounceAnimation} var(--spinner-speed) ease-in-out infinite;
`;

// Ring variant
export const RingSpinner = styled(SpinnerElement)`
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid currentColor;
  border-bottom: 3px solid currentColor;
  border-radius: 50%;
  animation: ${ringAnimation} var(--spinner-speed) linear infinite;
`;

// Fold variant
export const FoldSpinner = styled(SpinnerElement)`
  background-color: currentColor;
  transform-style: preserve-3d;
  animation: ${foldAnimation} var(--spinner-speed) ease-in-out infinite;
`;

// Loading text
export const LoadingText = styled.span<LoadingSpinnerStyleProps>`
  ${({ size = "medium" }) => {
    const sizeConfig = sizeMap[size];
    return css`
      font-size: ${sizeConfig.fontSize};
    `;
  }}

  ${({ color = "primary" }) => css`
    color: ${colorMap[color]};
  `}
  
  font-weight: 500;
  margin-top: 4px;
`;
